import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginWithSupabase, getAuthToken, clearAuthToken, supabase } from '../apiClient';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'issuer' | 'holder' | 'verifier';
  tenantId?: string;
  tenantName?: string;
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  logo?: string;
  primaryColor: string;
  status: 'active' | 'suspended';
  createdAt: string;
}

export interface FieldPosition {
  id: string;
  fieldName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  color: string;
}

export interface CredentialTemplate {
  id: string;
  name: string;
  width: number;
  height: number;
  backgroundImage?: string;
  backgroundColor: string;
  fields: FieldPosition[];
}

// Enhanced Schema Field interface
export interface SchemaField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'email' | 'date' | 'number' | 'textarea' | 'select' | 'boolean' | 'url' | 'phone';
  required: boolean;
  hidden: boolean;
  readOnly: boolean;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
  };
  options?: string[]; // For select fields
  permissions: {
    viewableByHolder: boolean;
    editableByHolder: boolean;
    viewableByVerifier: boolean;
    requiredForIssuance: boolean;
  };
  order: number;
}

// Enhanced Schema interface
export interface Schema {
  id: string;
  name: string;
  description: string;
  category: string;
  version: number;
  fields: SchemaField[];
  status: 'draft' | 'active' | 'deprecated';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  tenantId: string;
}

export interface Credential {
  id: string;
  title: string;
  issuer: string;
  issuerLogo: string;
  issuedDate: string;
  expiryDate: string | null;
  status: 'active' | 'expired' | 'revoked';
  category: string;
  verificationCount: number;
  lastVerified: string;
  fields: Record<string, string>;
  templateId?: string;
  schemaId?: string;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing Supabase session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          // Load user data from localStorage (set during login)
          const savedUser = localStorage.getItem('credential-capital-user');
          const savedTenant = localStorage.getItem('credential-capital-tenant');
          
          if (savedUser) {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            
            if (savedTenant) {
              const parsedTenant = JSON.parse(savedTenant);
              setTenant(parsedTenant);
            }
          }
        } else {
          // No session, clear any stale data
          localStorage.removeItem('credential-capital-user');
          localStorage.removeItem('credential-capital-tenant');
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_role');
          localStorage.removeItem('organization_id');
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setError('Session check failed');
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // User signed in - data should already be set by login function
          const savedUser = localStorage.getItem('credential-capital-user');
          if (savedUser) {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            
            const savedTenant = localStorage.getItem('credential-capital-tenant');
            if (savedTenant) {
              const parsedTenant = JSON.parse(savedTenant);
              setTenant(parsedTenant);
            }
          }
        } else if (event === 'SIGNED_OUT') {
          // User signed out
          setUser(null);
          setTenant(null);
          setIsAuthenticated(false);
          setError(null);
          // Clear localStorage
          localStorage.removeItem('credential-capital-user');
          localStorage.removeItem('credential-capital-tenant');
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_role');
          localStorage.removeItem('organization_id');
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await loginWithSupabase(email, password);
      if (result.success) {
        // User data is already set in localStorage by loginWithSupabase
        const savedUser = localStorage.getItem('credential-capital-user');
        const savedTenant = localStorage.getItem('credential-capital-tenant');
        
        if (savedUser) {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
          
          if (savedTenant) {
            const parsedTenant = JSON.parse(savedTenant);
            setTenant(parsedTenant);
          }
        }
        
        return true;
      } else {
        setError(result.error || 'Login failed');
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred during login');
      setIsAuthenticated(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear local state and storage
      clearAuthToken();
      setUser(null);
      setTenant(null);
      setIsAuthenticated(false);
      setError(null);
      
      // Clear all auth-related localStorage items
      localStorage.removeItem('credential-capital-user');
      localStorage.removeItem('credential-capital-tenant');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_role');
      localStorage.removeItem('organization_id');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Logout failed');
    }
  };

  const value = {
    user,
    tenant,
    login,
    logout,
    isLoading,
    isAuthenticated,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 